import React, { useEffect, useState } from 'react';
import Colors from '../../../themes/Colors';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { getDomain } from '../../../services/utils';

import logoLatinad from '../../../images/planner.png';
import logoFenix from '../../../images/whiteLabel/fenix-publinet.png';
import logoGrupovia from '../../../images/whiteLabel/grupo-via.svg';
import logoMediaShake from '../../../images/whiteLabel/media-shake.png';
import logoSmartFit from '../../../images/whiteLabel/smart-fit.png';
import logoDuoPrint from '../../../images/whiteLabel/duo-print.png';
import logoTerritorioDigital from '../../../images/whiteLabel/territorio-digital.png';
import logoMovImagen from '../../../images/whiteLabel/movimagen.png';
import logoYoda from '../../../images/whiteLabel/yoda.png';
import logoHakMedia from '../../../images/whiteLabel/hak-media.png';
import logoBlaster from '../../../images/whiteLabel/blaster.png';
import logoLaMediatica from '../../../images/whiteLabel/laMediatica.png';
import logoDv360 from '../../../images/whiteLabel/dv360.png';

const { Paragraph } = Typography;

const domain = getDomain(window.location.hostname);
const isLatinAd = domain === 'latinad';

const whiteLabelLogos = {
  latinad: logoLatinad,
  fenix: logoFenix,
  grupoVia: logoGrupovia,
  mediaShake: logoMediaShake,
  smartFit: logoSmartFit,
  duoPrint: logoDuoPrint,
  territorioDigital: logoTerritorioDigital,
  movImagen: logoMovImagen,
  yoda: logoYoda,
  hakMedia: logoHakMedia,
  blaster: logoBlaster,
  laMediatica: logoLaMediatica,
  dv360: logoDv360,
};

const whiteLabelLogo = whiteLabelLogos[domain] || logoLatinad;

export default function AILoaderRules({ onAnimationComplete }) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [isTyping, setIsTyping] = useState(false);

  const [floatingParticles] = useState(() =>
    [...Array(25)].map((_, i) => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      animationDelay: `${i * 0.5}s`,
    }))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          if (!isTyping) setIsTyping(true);
          return 100;
        }
        return prev + 2;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isTyping]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (onAnimationComplete) onAnimationComplete();
    }, 8000);

    return () => clearTimeout(timer);
  }, [onAnimationComplete]);

  const accentColor = isLatinAd ? '#60A5FA' : Colors.primary;
  const textColor = isLatinAd ? '#60A5FA' : Colors.primary;
  const bgStyle = isLatinAd
    ? 'linear-gradient(to bottom right, #0F172A, #1E3A8A, #0F172A)'
    : Colors.background;

  return (
    <div className="ai-loader-rules-container">
      <div className="loader-content">
        <div className="logo-container">
          <div className="particles-background">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="star-particle-rules"
                style={{
                  left: `${50 + 40 * Math.cos((i * 30 * Math.PI) / 180)}%`,
                  top: `${50 + 40 * Math.sin((i * 30 * Math.PI) / 180)}%`,
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            ))}
          </div>

          <div className="logo-svg-wrapper">
            {isLatinAd ? (
              <svg viewBox="0 0 1139 1139" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="logoGradientRules" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#60A5FA" />
                    <stop offset="50%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#1D4ED8" />
                  </linearGradient>
                </defs>
                <rect width="1139" height="1139" fill="transparent" />
                <g>
                  <rect x="503.32" y="503.32" width="132.36" height="132.36" fill="url(#logoGradientRules)" style={{ opacity: isTyping ? 1 : progress > 10 ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }} />
                  <rect x="701.86" y="701.86" width="66.18" height="66.18" fill="url(#logoGradientRules)" style={{ opacity: isTyping ? 1 : progress > 30 ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }} />
                  <polygon points="437.14,503.32 370.97,503.32 370.97,768.03 635.68,768.03 635.68,701.86 437.14,701.86" fill="url(#logoGradientRules)" style={{ opacity: isTyping ? 1 : progress > 50 ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }} />
                  <rect x="370.97" y="370.97" width="66.18" height="66.18" fill="url(#logoGradientRules)" style={{ opacity: isTyping ? 1 : progress > 70 ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }} />
                  <polygon points="503.32,370.97 503.32,437.14 701.86,437.14 701.86,635.68 768.03,635.68 768.03,370.97" fill="url(#logoGradientRules)" style={{ opacity: isTyping ? 1 : progress > 90 ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }} />
                </g>
              </svg>
            ) : (
              <img src={whiteLabelLogo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            )}
          </div>

          <div className="rotating-ring-rules" />
          <div className="pulsing-ring-rules" />
        </div>

        <div className="loader-actions">
          <div style={{ maxWidth: '672px', width: '100%', padding: '0 1rem' }}>
            <Paragraph style={{ color: textColor, fontSize: '14px', lineHeight: '1.6', textAlign: 'center' }}>
              {t('Optimizing campaign...')}
            </Paragraph>
          </div>
        </div>

        <div>
          {floatingParticles.map((particle, i) => (
            <div
              key={`particle-${i}`}
              className="float-particle-rules"
              style={{
                left: particle.left,
                top: particle.top,
                animationDelay: particle.animationDelay,
              }}
            />
          ))}
        </div>
      </div>
      <style>{`
          @keyframes float-rules {
              0%, 100% { transform: translateY(0px) scale(1); opacity: 0.4; }
              50% { transform: translateY(-20px) scale(1.2); opacity: 0.8; }
          }

          .ai-loader-rules-container {
              display: flex;
              padding: 50px 0;
              align-items: center;
              justify-content: center;
              min-height: 100%;
              background: ${bgStyle};
              position: relative;
              overflow: hidden;
              border-radius: 10px;
          }

          .ai-loader-rules-container .loader-content {
              width: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
              position: relative;
          }

          .ai-loader-rules-container .logo-container {
              position: relative;
              z-index: 10;
              width: 8rem;
              height: 8rem;
              display: flex;
              align-items: center;
              justify-content: center;
          }

          .ai-loader-rules-container .logo-svg-wrapper {
              position: relative;
              width: 6rem;
              height: 6rem;
          }

          .rotating-ring-rules {
              position: absolute;
              inset: 0;
              border-radius: 9999px;
              border-width: 2px;
              border-color: transparent;
              border-top-color: ${accentColor};
              border-right-color: ${accentColor};
              animation: spin-rules 3s linear infinite;
          }

          .pulsing-ring-rules {
              position: absolute;
              inset: 0;
              border-radius: 9999px;
              border: 1px solid ${accentColor}4D;
              animation: ping-rules 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
          }

          .ai-loader-rules-container .particles-background {
              position: absolute;
              width: 24rem;
              height: 24rem;
              left: 50%;
              top: 50%;
              transform: translate(-50%, -50%);
          }

          .star-particle-rules, .float-particle-rules {
              position: absolute;
              border-radius: 9999px;
          }

          .star-particle-rules {
              width: 4px;
              height: 4px;
              background-color: ${accentColor};
              animation: pulse-rules 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }

          .float-particle-rules {
              width: 4px;
              height: 4px;
              background-color: ${accentColor};
              opacity: 0.4;
              animation: float-rules 3s ease-in-out infinite;
          }

          .ai-loader-rules-container .loader-actions {
              display: flex;
              flex-direction: column;
              align-items: center;
              margin-top: 2rem;
          }

          @keyframes spin-rules {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
          }

          @keyframes ping-rules {
              75%, 100% { transform: scale(1.5); opacity: 0; }
          }

          @keyframes pulse-rules {
              50% { opacity: .5; }
          }
          `}
      </style>
    </div>
  );
}
